import { createFileRoute, Outlet, redirect, useLocation } from '@tanstack/react-router'
import React, { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from '@tanstack/react-router'
import { motion } from 'motion/react'
import Sidebar from '@/components/workspace/sidebar'
import Header from '@/components/workspace/header'
import { FullScreenLoader } from '@/components/ui/FullScreenLoader'
import { ProjectsProvider } from '@/providers/ProjectsProvider'
import { getSession } from '@/lib/auth-client'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { useSubscription } from '@/hooks/useSubscription'
import { PaymentFailureBanner } from '@/components/settings/sections/billing/PaymentFailureBanner'

export const Route = createFileRoute('/workspace')({
  beforeLoad: async ({ location }) => {
    try {
      const session = await getSession()
      if (!session?.data?.user) {
        throw redirect({ to: '/login', search: { redirect: location.href } })
      }
    } catch (e) {
      if (e instanceof Error && 'to' in (e as unknown as Record<string, unknown>)) throw e
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
  },
  component: WorkspaceLayout,
})

function WorkspaceLayout() {
  const { user, isLoading, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const [isHydrated, setIsHydrated] = useState(false)
  const openSettings = useSettingsStore((s) => s.open)
  const { isPastDue, isUnpaid } = useSubscription()

  // Hydration safety - prevent SSR mismatch
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // Redirect to login if not authenticated (in useEffect to avoid render issues)
  useEffect(() => {
    if (isHydrated && !isLoading && !user) {
      navigate({ to: '/login' })
    }
  }, [user, isLoading, navigate, isHydrated])

  // Always show loading during hydration to prevent mismatch
  if (!isHydrated || isLoading) {
    return <FullScreenLoader />
  }

  // Show redirecting message if not authenticated
  if (!user) {
    return <FullScreenLoader />
  }

  return (
    <ProjectsProvider>
      <div className="flex h-screen bg-background text-foreground">
        {/* Left Sidebar - Fixed Position */}
        <div className="fixed left-0 top-0 h-screen z-10">
          <Sidebar user={user} onLogout={logout} />
        </div>

        {/* Right Section - With left margin for sidebar */}
        <div className="relative flex min-w-0 flex-col bg-background ml-[248px] flex-1">
          {/* Top Navigation Bar */}
          <div className="sticky left-0 right-0 top-0 z-40">
            <Header />
          </div>

          {/* Payment failure banner — shown globally in workspace */}
          {isPastDue && (
            <div className="px-6 pt-4">
              <PaymentFailureBanner
                status="PAST_DUE"
                onUpdatePayment={() => openSettings('billing')}
              />
            </div>
          )}
          {isUnpaid && (
            <div className="px-6 pt-4">
              <PaymentFailureBanner
                status="UNPAID"
                onUpdatePayment={() => openSettings('billing')}
              />
            </div>
          )}

          {/* Main Content */}
          <div className="min-h-0 flex-1 overflow-y-auto p-6 pt-6">
            <motion.div
              key={pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <Outlet />
            </motion.div>
          </div>
        </div>
      </div>
    </ProjectsProvider>
  )
}
